"use client";

import { useQuery } from "convex/react";
import { useMemo } from "react";
import { useQueryNow } from "@/hooks/use-now";
import { useNowQuery } from "@/hooks/use-now-query";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { DashboardAudience } from "./navigation/account-type-policy";
import { isFreightCarrierAccount } from "./navigation/account-type-policy";

export function useOpenQuoteCount(enabled: boolean): number | undefined {
  const queryNow = useQueryNow();
  return useNowQuery(
    api.quotes.dashboardStatus.openCountForBuyer,
    enabled && queryNow !== undefined ? { now: queryNow } : "skip",
  );
}

export function useNavBadgeCounts(
  companyId: Id<"companies"> | undefined,
  audience: DashboardAudience,
) {
  const hasMarketplaceAccount = audience.accountTypes.some(
    (accountType) => accountType !== "freight_carrier",
  );
  const hasFreightAccount = isFreightCarrierAccount(audience);
  const lowStock = useQuery(
    api.dashboards.notifications.getLowStockBadgeCount,
    companyId && hasMarketplaceAccount ? { companyId } : "skip",
  );
  const openQuotes = useOpenQuoteCount(Boolean(companyId) && hasMarketplaceAccount);
  const carrierCounts = useQuery(
    api.freight.carrierDashboard.getCarrierNavCounts,
    hasFreightAccount ? {} : "skip",
  );

  return useMemo(() => {
    const badgeCounts: Record<string, number> = {};
    if (lowStock) {
      badgeCounts["/dashboard/inventory"] = lowStock;
    }
    if (openQuotes) {
      badgeCounts["/dashboard/quotes"] = openQuotes;
    }
    if (carrierCounts) {
      if (carrierCounts.activeShipments.count) {
        badgeCounts["/dashboard/carrier/loads"] = carrierCounts.activeShipments.count;
      }
      if (carrierCounts.pendingBids.count) {
        badgeCounts["/dashboard/carrier/bids"] = carrierCounts.pendingBids.count;
      }
    }
    return badgeCounts;
  }, [carrierCounts, lowStock, openQuotes]);
}
